import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Box,
  Typography,
  Chip,
  Avatar,
  Alert,
  CircularProgress,
} from '@mui/material';
import {
  CheckCircle,
  Cancel,
  Person,
} from '@mui/icons-material';
import { useSelector, useDispatch } from 'react-redux';
import { RootState } from '../../store';
import { closeDialog, addNotification } from '../../store/slices/uiSlice';
import { updateItem } from '../../store/slices/inventorySlice';
import { FirestoreService } from '../../firebase/firestoreService';
import { InventoryItem } from '../../types/models';
import { format } from 'date-fns';

const CheckInOutDialog: React.FC = () => {
  const dispatch = useDispatch();
  const { dialog } = useSelector((state: RootState) => state.ui);
  const { user, organization } = useSelector((state: RootState) => state.auth);
  
  const [userName, setUserName] = useState(user?.firstName ? `${user.firstName} ${user.lastName}`.trim() : '');
  const [notes, setNotes] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const item = dialog.data as InventoryItem;
  const isCheckIn = dialog.type === 'checkin';
  const isOpen = dialog.open && (dialog.type === 'checkin' || dialog.type === 'checkout');

  const handleClose = () => {
    dispatch(closeDialog());
    setUserName(user?.firstName ? `${user.firstName} ${user.lastName}`.trim() : '');
    setNotes('');
    setError(null);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!isCheckIn && !userName.trim()) {
      setError('User name is required');
      return;
    }

    if (!item?.id) {
      setError('Invalid item');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      if (isCheckIn) {
        await FirestoreService.checkInItem(item.id, userName.trim(), notes.trim() || undefined, item.itemName, organization?.id);
        dispatch(addNotification({
          message: `${item.itemName} checked in successfully`,
          type: 'success',
        }));
      } else {
        await FirestoreService.checkOutItem(item.id, userName.trim(), notes.trim() || undefined, item.itemName, organization?.id);
        dispatch(addNotification({
          message: `${item.itemName} checked out successfully`,
          type: 'success',
        }));
      }

      // Update local state immediately for optimistic UI
      const updatedItem: InventoryItem = {
        ...item,
        checkedOut: !isCheckIn,
        lastUpdated: Date.now() / 1000,
        ...(isCheckIn 
          ? { checkedOutTo: undefined, checkedOutDate: undefined }
          : { checkedOutTo: userName.trim(), checkedOutDate: Date.now() / 1000 }
        )
      };
      dispatch(updateItem(updatedItem));

      handleClose();
    } catch (error: any) {
      console.error('Error updating item:', error);
      setError(error.message || `Failed to ${isCheckIn ? 'check in' : 'check out'} item`);
      dispatch(addNotification({
        message: `Failed to ${isCheckIn ? 'check in' : 'check out'} ${item.itemName}`,
        type: 'error',
      }));
    } finally {
      setLoading(false);
    }
  };

  const getLastActivity = () => {
    if (!item?.checkOutHistory || item.checkOutHistory.length === 0) {
      return null;
    }

    const lastEvent = item.checkOutHistory
      .sort((a, b) => b.eventDate - a.eventDate)[0];

    return {
      user: lastEvent.userName,
      date: format(new Date(lastEvent.eventDate * 1000), 'MMM d, yyyy h:mm a'),
      action: lastEvent.checkedOut ? 'Checked out' : 'Checked in',
    };
  };

  if (!isOpen || !item) {
    return null;
  }

  const lastActivity = getLastActivity();

  return (
    <Dialog open={isOpen} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          {isCheckIn ? (
            <CheckCircle color="success" />
          ) : (
            <Cancel color="error" />
          )}
          <Box>
            <Typography variant="h6">
              {isCheckIn ? 'Check In' : 'Check Out'} Item
            </Typography>
            <Typography variant="subtitle2" color="text.secondary">
              {item.itemName}
            </Typography>
          </Box>
        </Box>
      </DialogTitle>

      <DialogContent>
        {/* Current Status */}
        <Box sx={{ mb: 3 }}>
          <Typography variant="subtitle2" gutterBottom>
            Current Status
          </Typography>
          <Chip
            label={item.checkedOut ? 'Checked Out' : 'Available'}
            color={item.checkedOut ? 'error' : 'success'}
            icon={item.checkedOut ? <Cancel /> : <CheckCircle />}
          />
          
          {item.itemGroup && (
            <Chip
              label={item.itemGroup}
              variant="outlined"
              sx={{ ml: 1 }}
            />
          )}
        </Box>

        {/* Last Activity / Current Holder */}
        {lastActivity && (
          <Box sx={{ mb: 3, p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
            <Typography variant="subtitle2" gutterBottom>
              {isCheckIn ? 'Currently Checked Out To' : 'Last Activity'}
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <Avatar sx={{ width: 24, height: 24, mr: 1, fontSize: '0.75rem' }}>
                {lastActivity.user.charAt(0).toUpperCase()}
              </Avatar>
              <Typography variant="body2">
                {isCheckIn ? 
                  `${lastActivity.user} (since ${lastActivity.date})` :
                  `${lastActivity.action} by ${lastActivity.user} on ${lastActivity.date}`
                }
              </Typography>
            </Box>
          </Box>
        )}

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        <Box component="form" onSubmit={handleSubmit}>
          <TextField
            autoFocus={!isCheckIn}
            margin="normal"
            label={isCheckIn ? "Checked In By" : "Check Out To"}
            fullWidth
            required={!isCheckIn}
            value={userName}
            onChange={isCheckIn ? undefined : (e) => setUserName(e.target.value)}
            placeholder={isCheckIn ? "" : "Enter student/person name"}
            InputProps={{
              startAdornment: <Person sx={{ mr: 1, color: 'action.active' }} />,
              readOnly: isCheckIn,
              sx: isCheckIn ? { 
                '& input': { 
                  cursor: 'default',
                  '&:focus': { cursor: 'default' }
                }
              } : {}
            }}
            disabled={loading}
            sx={isCheckIn ? {
              '& .MuiInputBase-input': {
                color: 'text.secondary',
                cursor: 'default !important',
                pointerEvents: 'none'
              }
            } : {}}
            helperText={isCheckIn ? "Item will be checked in by the current user" : "Who will have this item?"}
          />

          <TextField
            margin="normal"
            label="Notes (Optional)"
            fullWidth
            multiline
            rows={2}
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            placeholder={`Add any notes about this ${isCheckIn ? 'check-in' : 'check-out'}...`}
            disabled={loading}
          />
        </Box>
      </DialogContent>

      <DialogActions>
        <Button onClick={handleClose} disabled={loading}>
          Cancel
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          color={isCheckIn ? 'success' : 'primary'}
          disabled={loading || !userName.trim()}
          startIcon={loading ? <CircularProgress size={20} /> : undefined}
        >
          {loading 
            ? `${isCheckIn ? 'Checking In' : 'Checking Out'}...`
            : `${isCheckIn ? 'Check In' : 'Check Out'}`
          }
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default CheckInOutDialog;